import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import { strings } from './strings'

const LanguageContext = createContext(null)

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem('portal-language')
      if (stored) return stored
      // Default: Chinese for parents, English for students
      // Check sessionStorage for user type (set during auth)
      const auth = sessionStorage.getItem('portal-auth')
      if (auth) {
        const parsed = JSON.parse(auth)
        if (parsed.userType === 'student') return 'en'
      }
      return 'cn'
    } catch {
      return 'cn'
    }
  })

  // An explicit toggle this session wins over any later session default even
  // when localStorage is blocked (where the stored-choice check can't see it).
  const explicitChoice = useRef(false)

  // Session default for surfaces that know their audience (e.g. the TA
  // portal defaults to English). Never overrides an explicit user choice
  // (stored portal-language, or a toggle made this session) and never
  // persists one itself.
  const setDefaultLanguage = useCallback((lang) => {
    if (explicitChoice.current) return
    try {
      if (localStorage.getItem('portal-language')) return
    } catch {
      // localStorage unavailable — still apply the session default
    }
    setLanguage((prev) => (prev === lang ? prev : lang))
  }, [])

  const toggleLanguage = useCallback(() => {
    explicitChoice.current = true
    setLanguage(prev => {
      const next = prev === 'en' ? 'cn' : 'en'
      try {
        localStorage.setItem('portal-language', next)
      } catch {
        // localStorage unavailable
      }
      return next
    })
  }, [])

  const t = useCallback((key) => {
    const entry = strings[key]
    if (!entry) return key
    return entry[language] || entry.en || key
  }, [language])

  const value = useMemo(
    () => ({ language, toggleLanguage, setDefaultLanguage, t }),
    [language, toggleLanguage, setDefaultLanguage, t],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider')
  return ctx
}
